import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ClipboardList, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft,
  Shield, FileText, Gauge, UserCheck, Loader2
} from 'lucide-react';
import PageHeader from '../shared/PageHeader';
import StatusBadge from '../shared/StatusBadge';
import ScorePill from '../shared/ScorePill';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STAGES = [
  { id: 'INITIATION', label: 'Basics', icon: FileText },
  { id: 'INSTRUMENTATION_CHECKLIST', label: 'Instrumentation', icon: ClipboardList },
  { id: 'ASSESSMENT_RESULT', label: 'Assessment', icon: Gauge },
  { id: 'SLA_REGISTRATION', label: 'SLA Registration', icon: Shield },
  { id: 'OPERATOR_APPROVAL', label: 'Approval', icon: UserCheck }
];

const AppIntake = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeIntake, setActiveIntake] = useState(null);
  const [intakeDetail, setIntakeDetail] = useState(null);
  const [selectedPipelineStage, setSelectedPipelineStage] = useState(null);

  // New intake form
  const [formData, setFormData] = useState({
    application_name: '',
    description: '',
    technology_stack: '',
    owning_team: '',
    primary_contact: '',
    secondary_contact: '',
    base_url: ''
  });

  // SLA form
  const [slaData, setSlaData] = useState({
    availability_target: 99.5,
    response_time_target: 500,
    error_rate_budget: 0.1
  });

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/appsupport/intake/pipeline`);
      const d = await res.json();
      setPipeline(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntakeDetail = async (intakeId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/appsupport/intake/${intakeId}`);
      const d = await res.json();
      setIntakeDetail(d);
      // Set stage based on current stage
      const stageMap = { 'INSTRUMENTATION_CHECKLIST': 1, 'ASSESSMENT_PENDING': 2, 'ASSESSMENT_FAILED': 2, 'SLA_REGISTRATION': 3, 'OPERATOR_APPROVAL': 4, 'COMPLETE': 4, 'REJECTED': 4 };
      setActiveStage(stageMap[d.current_stage] || 0);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchPipeline(); }, []);

  const startIntake = async () => {
    if (!formData.application_name || !formData.primary_contact || !formData.owning_team) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${BACKEND_URL}/api/appsupport/intake/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          technology_stack: formData.technology_stack.split(',').map(s => s.trim()).filter(Boolean)
        })
      });
      const d = await res.json();
      if (res.ok) {
        toast.success('Intake started successfully');
        setActiveIntake(d.intake_id);
        await fetchIntakeDetail(d.intake_id);
        setActiveStage(1);
        fetchPipeline();
      } else {
        toast.error(d.detail || 'Failed to start intake');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const updateChecklist = async (itemId, completed) => {
    if (!activeIntake) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/appsupport/intake/${activeIntake}/checklist/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed, evidence_note: completed ? 'Completed' : null })
      });
      const d = await res.json();
      if (d.assessment_triggered) {
        toast.success('Assessment triggered automatically!');
        setActiveStage(2);
      }
      await fetchIntakeDetail(activeIntake);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const registerSla = async () => {
    if (!activeIntake) return;
    try {
      setSubmitting(true);
      const res = await fetch(`${BACKEND_URL}/api/appsupport/intake/${activeIntake}/sla`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slaData)
      });
      const d = await res.json();
      if (res.ok) {
        toast.success('SLA registered');
        setActiveStage(4);
        await fetchIntakeDetail(activeIntake);
        fetchPipeline();
      } else {
        toast.error(d.detail || 'Failed to register SLA');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const approveIntake = async (decision) => {
    if (!activeIntake) return;
    try {
      setSubmitting(true);
      const res = await fetch(`${BACKEND_URL}/api/appsupport/intake/${activeIntake}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes: 'Approved via UI', operator_id: 'operator-001' })
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message);
        await fetchIntakeDetail(activeIntake);
        fetchPipeline();
      } else {
        toast.error(d.detail || 'Action failed');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const selectExistingIntake = (intake) => {
    setActiveIntake(intake.intake_id);
    fetchIntakeDetail(intake.intake_id);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="application-intake">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <PageHeader title="Application Intake" subtitle="Governed onboarding workflow" onRefresh={fetchPipeline} />

        {/* Pipeline Summary */}
        {pipeline && !activeIntake && (
          <div className="mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {Object.entries(pipeline.counts || {}).filter(([k]) => pipeline.counts[k] > 0).map(([stage, count]) => (
                <Card
                  key={stage}
                  className={`cursor-pointer transition-all duration-150 ${
                    selectedPipelineStage === stage
                      ? 'border-[hsl(var(--ring))] ring-1 ring-[hsl(var(--ring))]/30 shadow-sm'
                      : 'hover:border-[hsl(var(--ring))]/50'
                  }`}
                  onClick={() => setSelectedPipelineStage(selectedPipelineStage === stage ? null : stage)}
                  data-testid={`pipeline-tile-${stage.toLowerCase().replace(/_/g, '-')}`}
                >
                  <CardContent className="p-4">
                    <div className="text-xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{count}</div>
                    <div className="text-xs text-muted-foreground">{stage.replace(/_/g, ' ')}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Stage Preview Panel */}
            {selectedPipelineStage && pipeline.stages?.[selectedPipelineStage]?.length > 0 && (
              <Card className="border-[hsl(var(--ring))]/30 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="pipeline-stage-preview">
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[hsl(var(--ring))]"></span>
                      {selectedPipelineStage.replace(/_/g, ' ')}
                      <Badge variant="secondary" className="ml-1 text-xs">{pipeline.stages[selectedPipelineStage].length}</Badge>
                    </CardTitle>
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setSelectedPipelineStage(null)} data-testid="pipeline-stage-preview-close">
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {pipeline.stages[selectedPipelineStage].map((intake) => (
                      <div
                        key={intake.intake_id}
                        className="px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
                        onClick={() => selectExistingIntake(intake)}
                        data-testid="pipeline-stage-preview-item"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground">{intake.application_name}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <StatusBadge status={intake.current_stage} />
                            <span className="text-xs text-muted-foreground font-mono">{intake.technology_stack?.join(', ')}</span>
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 ml-3" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Existing intakes list */}
        {!activeIntake && pipeline && (
          <div className="mb-6">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold">Active Intakes</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {Object.values(pipeline.stages || {}).flat().map((intake) => (
                    <div key={intake.intake_id} className="px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors duration-150 cursor-pointer" onClick={() => selectExistingIntake(intake)}>
                      <div>
                        <div className="text-sm font-medium text-foreground">{intake.application_name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge status={intake.current_stage} />
                          <span className="text-xs text-muted-foreground">{intake.technology_stack?.join(', ')}</span>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* New Intake / Active Intake Flow */}
        <div className="grid grid-cols-12 gap-4">
          {/* Steps sidebar */}
          <div className="col-span-12 lg:col-span-4">
            <Card>
              <CardContent className="p-4 space-y-2">
                {STAGES.map((stage, idx) => {
                  const Icon = stage.icon;
                  const isCompleted = idx < activeStage;
                  const isCurrent = idx === activeStage;
                  return (
                    <div key={stage.id}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors duration-150
                        ${isCurrent ? 'bg-accent border border-[hsl(var(--ring))]/30' : isCompleted ? 'bg-muted/50' : ''}
                      `}
                      data-testid={`intake-step-${idx}`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                        ${isCompleted ? 'bg-[hsl(var(--status-ok))] text-white' : isCurrent ? 'bg-[hsl(var(--ring))] text-white' : 'bg-muted text-muted-foreground'}
                      `}>
                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : idx + 1}
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>{stage.label}</div>
                      </div>
                    </div>
                  );
                })}
                {!activeIntake && (
                  <Button className="w-full mt-4" onClick={() => setActiveStage(0)} data-testid="intake-new-button">
                    New Application Intake
                  </Button>
                )}
                {activeIntake && (
                  <Button variant="outline" className="w-full mt-4" onClick={() => { setActiveIntake(null); setIntakeDetail(null); setActiveStage(0); }}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to List
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Form area */}
          <div className="col-span-12 lg:col-span-8">
            <Card>
              <CardContent className="p-6">
                {/* Stage 0: Basics */}
                {activeStage === 0 && !activeIntake && (
                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Application Basics</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-foreground">Application Name *</label>
                        <Input value={formData.application_name} onChange={(e) => setFormData({...formData, application_name: e.target.value})} placeholder="e.g. Invoice Ingest Service" className="mt-1" data-testid="intake-app-name-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground">Description</label>
                        <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Brief description..." className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground">Technology Stack *</label>
                        <Input value={formData.technology_stack} onChange={(e) => setFormData({...formData, technology_stack: e.target.value})} placeholder="e.g. .NET, C#, SQL Server" className="mt-1" />
                        <p className="text-xs text-muted-foreground mt-1">Comma-separated list</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-foreground">Owning Team *</label>
                          <Input value={formData.owning_team} onChange={(e) => setFormData({...formData, owning_team: e.target.value})} placeholder="e.g. Billing Operations" className="mt-1" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground">Primary Contact *</label>
                          <Input value={formData.primary_contact} onChange={(e) => setFormData({...formData, primary_contact: e.target.value})} placeholder="e.g. name@company.com" className="mt-1" data-testid="intake-contact-input" />
                        </div>
                      </div>
                    </div>
                    <Button onClick={startIntake} disabled={submitting} data-testid="intake-next-button">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Start Intake
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Stage 1: Checklist */}
                {activeStage === 1 && intakeDetail && (
                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Instrumentation Checklist</h3>
                    <p className="text-sm text-muted-foreground">Complete all items to trigger the supportability assessment.</p>
                    <div className="space-y-3">
                      {intakeDetail.checklist_items?.map((item) => (
                        <div key={item.item_id} className={`p-4 rounded-lg border ${item.completed ? 'border-[hsl(var(--status-ok))]/30 bg-[hsl(var(--status-ok))]/5' : 'border-border'}`}>
                          <div className="flex items-start gap-3">
                            <Checkbox checked={item.completed} onCheckedChange={(checked) => updateChecklist(item.item_id, checked)} />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-foreground">{item.dimension.replace(/_/g, ' ')}</div>
                              <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                              {item.completed_at && <p className="text-xs text-[hsl(var(--status-ok))] mt-1">Completed</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stage 2: Assessment */}
                {activeStage === 2 && intakeDetail && (
                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Assessment Result</h3>
                    {intakeDetail.assessment ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-4xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>{intakeDetail.assessment.score}</div>
                            <StatusBadge status={intakeDetail.assessment.label} />
                          </div>
                          <div className="text-sm text-muted-foreground flex-1">{intakeDetail.assessment.overall_remediation}</div>
                        </div>
                        <Separator />
                        {intakeDetail.assessment.dimension_results?.map((d) => (
                          <div key={d.dimension} className="flex items-center justify-between text-sm py-1">
                            <span className="text-muted-foreground">{d.dimension.replace(/_/g, ' ')}</span>
                            <div className="flex items-center gap-2">
                              <ScorePill score={d.score} />
                              {d.present ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-ok))]" /> : <AlertCircle className="h-4 w-4 text-[hsl(var(--status-crit))]" />}
                            </div>
                          </div>
                        ))}
                        {intakeDetail.assessment.score >= 40 && (
                          <Button onClick={() => setActiveStage(3)} data-testid="intake-next-button">
                            Proceed to SLA Registration <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">Assessment pending...</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Stage 3: SLA */}
                {activeStage === 3 && (
                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>SLA Registration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">Availability Target (%)</label>
                        <Input type="number" step="0.1" value={slaData.availability_target} onChange={(e) => setSlaData({...slaData, availability_target: parseFloat(e.target.value)})} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Response Time Target (ms)</label>
                        <Input type="number" value={slaData.response_time_target} onChange={(e) => setSlaData({...slaData, response_time_target: parseInt(e.target.value)})} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Error Rate Budget (%)</label>
                        <Input type="number" step="0.01" value={slaData.error_rate_budget} onChange={(e) => setSlaData({...slaData, error_rate_budget: parseFloat(e.target.value)})} className="mt-1" />
                      </div>
                    </div>
                    <Button onClick={registerSla} disabled={submitting} data-testid="intake-submit-button">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Register SLA <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Stage 4: Approval */}
                {activeStage === 4 && intakeDetail && (
                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Operator Approval</h3>
                    {intakeDetail.current_stage === 'COMPLETE' ? (
                      <div className="text-center py-8">
                        <CheckCircle2 className="h-12 w-12 text-[hsl(var(--status-ok))] mx-auto mb-3" />
                        <p className="text-lg font-semibold text-foreground">Application Approved</p>
                        <p className="text-sm text-muted-foreground">Now in Active Monitoring</p>
                      </div>
                    ) : intakeDetail.current_stage === 'REJECTED' ? (
                      <div className="text-center py-8">
                        <AlertCircle className="h-12 w-12 text-[hsl(var(--status-crit))] mx-auto mb-3" />
                        <p className="text-lg font-semibold text-foreground">Intake Rejected</p>
                        <p className="text-sm text-muted-foreground">{intakeDetail.operator_notes}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 rounded-lg border border-[hsl(var(--ring))]/30 bg-accent/50">
                          <p className="text-sm"><strong>Application:</strong> {intakeDetail.application?.name}</p>
                          <p className="text-sm"><strong>Score:</strong> {intakeDetail.application?.supportability_score} ({intakeDetail.application?.supportability_label?.replace(/_/g, ' ')})</p>
                          <p className="text-sm"><strong>Team:</strong> {intakeDetail.application?.owning_team}</p>
                        </div>
                        <div className="flex gap-3">
                          <Button onClick={() => approveIntake('APPROVED')} disabled={submitting} className="bg-[hsl(var(--status-ok))] hover:bg-[hsl(var(--status-ok))]/90 text-white" data-testid="intake-approve-button">
                            Approve
                          </Button>
                          <Button variant="destructive" onClick={() => approveIntake('REJECTED')} disabled={submitting} data-testid="intake-reject-button">
                            Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Default view when no intake selected */}
                {!activeIntake && activeStage !== 0 && (
                  <div className="text-center py-12">
                    <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-foreground font-medium">Select an intake or start a new one</p>
                    <p className="text-sm text-muted-foreground mt-1">Click on an existing intake or start fresh</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppIntake;
